import { BudgetUserClient } from '@frontend/budget/api';
import { Budget } from '@frontend/budget/types';
import { EntityType, ResolvedEntity, formatDateTimeWithLocale } from '@frontend/common';
import { accountIdFromResolvedEntity } from '@frontend/components/common';
import { ObjectColumnsProps, defaultCellClassName } from '@frontend/components/list';
import { ReactiveObjectList } from '@frontend/components/list-v2';
import { Event, EventLevel } from '@frontend/events/types';
import { Application } from '@frontend/functionality/types';
import { CommonMessage, useLocale } from '@frontend/lang';
import { LaundryTransactionClient } from '@frontend/laundry-app/api';
import { LaundryTransaction } from '@frontend/laundry-app/types';
import { useModals } from '@frontend/modal';
import { useLicenseRepository } from '@frontend/repository';
import { TransactionLendingClient, TransactionPudoExtendedClient, TransactionVendingClient } from '@frontend/transaction/api';
import {
    HistoryEventDetails,
    TransactionHistory,
    TransactionStateBadge,
    getHistoryEventTitleClassName,
    mapEventDataToDisplay
} from '@frontend/transaction/common';
import { ExtendedLendingTransaction, ExtendedPudoTransaction, ExtendedVendingTransaction, TransactionStateName } from '@frontend/transaction/types';
import { IUser } from '@frontend/user/types';
import React from 'react';
import { FormattedMessage } from 'react-intl';
import { useNavigate } from 'react-router-dom';

import { NotificationPreferences } from '../../applications/address-book/contacts/modal/notification-preferences/notification-preferences.component';
import { ImageContainerWrapper } from '../../applications/modals/containers/image-wrapper/image-container-wrapper.components';
import { getVendingTransactionTypeDisplay } from '../../applications/vending/vending-utils';
import { MultiEntityButton } from '../../components/multi-entity-button/multi-entity-button.component';
import { PlatformDetailGrid } from '../../modal/platform-detail-grid/platform-detail-grid.component';
import { PlatformEventReferenceBadges } from '../../modal/platform-event-reference-badges/platform-event-reference-badges.component';
import { PlatformObjectBadge } from '../../modal/platform-object-badge/platform-object-badge.component';
import { useAppDispatch } from '../../redux/store';
import { BackupNotificationUserCard } from './backup-notification-user-card/backup-notification-user-card.component';
import { BudgetAvailabilityCard } from './budget-availability-card/budget-availability-card.component';
import { Items } from './items/items.component';
import { RolesAndLicenses } from './roles-and-licenses/roles-and-licenses.component';
import { BadgeCard } from './security/badge-card/badge-card.component';
import { BasicAuthCard } from './security/basic-auth-card/basic-auth-card.component';
import { ItsmeCard } from './security/itsme-card/itsme-card.component';
import { SupervisedUsersCard } from './supervised-users-card/supervised-users-card.component';
import { UserGroupCard } from './user-group-card/user-group-card.component';

const USER_DETAIL_ORDER = ['unique_identifier', 'first_name', 'last_name', 'email', 'mobile', 'language'] as const;
const CONTACT_DETAIL_ORDER = ['first_name', 'last_name', 'department', 'email', 'mobile', 'language'] as const;

export const getUserDetailOrder = (): string[] => [...USER_DETAIL_ORDER];
export const getContactDetailOrder = (): string[] => [...CONTACT_DETAIL_ORDER];
export const getUserDetailKeyOverwrite = () => new Map([['unique_identifier', CommonMessage.FORMS.UNIQUE_IDENTIFIER]]);

export const UserDetailsCard = ({ user, contact }: { user: IUser; contact?: boolean }) => (
    <div className='flex w-full gap-3'>
        <div className='bg-background-surface outline-border-default inline-flex w-full flex-col items-start justify-start gap-6 self-stretch rounded-xl p-4 outline-1 outline-offset-[-1px]'>
            <div className='text-foreground-default justify-start text-xl font-semibold'>{CommonMessage.DETAIL.DETAILS}</div>
            <PlatformDetailGrid
                object={user}
                cols={3}
                order={contact ? getContactDetailOrder() : getUserDetailOrder()}
                keyOverwrite={getUserDetailKeyOverwrite()}
            />
        </div>
        {!contact && (
            <ImageContainerWrapper
                entity={user}
                entity_type={EntityType.USER}
            />
        )}
    </div>
);

export const UserGeneralSection = ({ user }: { user: IUser }) => (
    <>
        <NotificationPreferences object={user} />
        <div className='flex w-full gap-4'>
            <UserGroupCard entity_id={user.id} />
            <BackupNotificationUserCard entity_id={user.id} />
        </div>
        <SupervisedUsersCard
            account_id={user.account_id}
            entity_id={user.id}
        />
    </>
);

export const ContactGeneralSection = ({ user }: { user: IUser }) => <NotificationPreferences object={user} />;

export const UserItemsSection = ({ user }: { user: IUser }) => <Items entity_id={user.id} />;

export const UserRolesAndLicensesSection = ({ user }: { user: IUser }) => <RolesAndLicenses entity_id={user.id} />;

export const UserSecuritySection = ({ user }: { user: IUser }) => (
    <div className='grid w-full grid-cols-2 gap-4'>
        <BasicAuthCard entity_id={user.id} />
        <BadgeCard entity_id={user.id} />
        <ItsmeCard entity_id={user.id} />
    </div>
);

export const UserBudgetsSection = ({ user }: { user: IUser }) => {
    const dispatch = useAppDispatch();
    return (
        <div className='flex w-full flex-col gap-4'>
            <BudgetAvailabilityCard
                accountId={user.account_id}
                userId={user.id}
            />
            <ReactiveObjectList<Budget>
                fetch={(arg) => BudgetUserClient.fetchUserBudgets(user.account_id, user.id, arg)}
                dispatch={dispatch}
                columns={{
                    columns: ['name', 'type', 'from_timestamp', 'to_timestamp'],
                    headerOverwrite: new Map([
                        ['name', CommonMessage.OBJECTS.BUDGET.DEFAULT],
                        ['from_timestamp', CommonMessage.OBJECTS.COMMON_FIELDS.VALID_FROM],
                        ['to_timestamp', CommonMessage.OBJECTS.COMMON_FIELDS.VALID_TO]
                    ]),
                    customCells: new Map([
                        [
                            'name',
                            (row) => (
                                <td className={defaultCellClassName}>
                                    <PlatformObjectBadge
                                        entityType={EntityType.BUDGET}
                                        id={row.row.original.id}
                                        display={row.row.original.name}
                                    />
                                </td>
                            )
                        ]
                    ])
                }}
                type={EntityType.BUDGET}
                tableProps={{ defaultHeight: true, hideFooter: true }}
            />
        </div>
    );
};

function createParticipantActivityCells<
    T extends {
        id: string;
        senders: ResolvedEntity[] | null;
        receivers: ResolvedEntity[] | null;
        state: TransactionStateName;
    }
>(): NonNullable<ObjectColumnsProps<T>['customCells']> {
    return new Map([
        [
            'senders',
            (row) => {
                if (!row.row.original.senders?.length) {
                    return (
                        <td className='text-brand-primary align-center truncate p-2 text-xs'>
                            <div className='text-foreground-default justify-start text-sm font-medium'>/</div>
                        </td>
                    );
                }
                const [first, ...rest] = row.row.original.senders;
                return (
                    <td
                        key={first.entity_id}
                        className='text-brand-primary align-center truncate p-2 text-xs'>
                        <div className='flex gap-2.5 truncate text-sm font-medium'>
                            <PlatformObjectBadge
                                id={first.entity_id}
                                entityType={EntityType.USER}
                                display={first.name}
                                foreign={first.foreign}
                                accountId={accountIdFromResolvedEntity(first)}
                            />
                            {rest.length > 0 && (
                                <MultiEntityButton
                                    entities={rest}
                                    render={(entity) => (
                                        <PlatformObjectBadge
                                            key={entity.entity_id}
                                            id={entity.entity_id}
                                            entityType={EntityType.USER}
                                            display={entity.name}
                                            foreign={entity.foreign}
                                            accountId={accountIdFromResolvedEntity(entity)}
                                        />
                                    )}
                                />
                            )}
                        </div>
                    </td>
                );
            }
        ],
        [
            'receivers',
            (row) => {
                if (!row.row.original.receivers?.length) {
                    return (
                        <td className='text-brand-primary align-center truncate p-2 text-xs'>
                            <div className='text-foreground-default justify-start text-sm font-medium'>/</div>
                        </td>
                    );
                }
                const [first, ...rest] = row.row.original.receivers;
                return (
                    <td
                        key={first.entity_id}
                        className='text-brand-primary align-center truncate p-2 text-xs'>
                        <div className='flex gap-2.5 truncate text-sm font-medium'>
                            <PlatformObjectBadge
                                id={first.entity_id}
                                entityType={EntityType.USER}
                                display={first.name}
                                foreign={first.foreign}
                                accountId={accountIdFromResolvedEntity(first)}
                            />
                            {rest.length > 0 && (
                                <MultiEntityButton
                                    entities={rest}
                                    render={(entity) => (
                                        <PlatformObjectBadge
                                            key={entity.entity_id}
                                            id={entity.entity_id}
                                            entityType={EntityType.USER}
                                            display={entity.name}
                                            foreign={entity.foreign}
                                            accountId={accountIdFromResolvedEntity(entity)}
                                        />
                                    )}
                                />
                            )}
                        </div>
                    </td>
                );
            }
        ],
        [
            'state',
            (row) => (
                <td
                    key={row.row.original.state}
                    className='text-foreground-default truncate p-2 text-sm font-medium'>
                    <TransactionStateBadge state={row.row.original.state} />
                </td>
            )
        ]
    ]);
}

const RecentTransactions = (
    <FormattedMessage
        id='user.recentTransactions'
        defaultMessage='Recent Transactions'
    />
);
const RecentShipments = (
    <FormattedMessage
        id='user.recentShipments'
        defaultMessage='Recent Shipments'
    />
);
const RecentVendings = (
    <FormattedMessage
        id='user.recentVendings'
        defaultMessage='Recent Vendings'
    />
);
const RecentUtilizations = (
    <FormattedMessage
        id='user.recentUtilizations'
        description='Label for recent lending utilizations shown in the user detail activity section'
        defaultMessage='Recent utilizations'
    />
);

export const UserActivityPudoSection = ({ user }: { user: IUser }) => {
    const dispatch = useAppDispatch();
    return (
        <ReactiveObjectList<ExtendedPudoTransaction>
            dispatch={dispatch}
            key={`recent-transactions-${user.id}`}
            label={RecentTransactions}
            fetch={(arg) =>
                TransactionPudoExtendedClient.fetchPudoExtendedTransactions({
                    ...arg,
                    account_id: user.account_id,
                    application: Application.PUDO,
                    sender_id: user.id,
                    receiver_id: user.id,
                    size: '5',
                    without_collis: 'true',
                    without_packages: 'true',
                    without_pin_codes: 'true'
                })
            }
            columns={{
                columns: ['receivers', 'senders', 'state'],
                headerOverwrite: new Map([
                    ['receivers', CommonMessage.OBJECTS.TRANSACTION.RECEIVERS],
                    ['senders', CommonMessage.OBJECTS.TRANSACTION.SENDERS],
                    ['state', CommonMessage.OBJECTS.COMMON_FIELDS.STATE]
                ]),
                customCells: createParticipantActivityCells<ExtendedPudoTransaction>()
            }}
            type={EntityType.TRANSACTION}
            tableProps={{ hideFooter: true, maxHeight: true }}
        />
    );
};

export const UserActivityLaundrySection = ({ user }: { user: IUser }) => {
    const dispatch = useAppDispatch();
    return (
        <ReactiveObjectList<LaundryTransaction>
            dispatch={dispatch}
            key={`recent-shipments-${user.id}`}
            label={RecentShipments}
            fetch={(arg) =>
                LaundryTransactionClient.fetchLaundryTransactions(user.account_id, {
                    ...arg,
                    application: Application.LAUNDRY,
                    user_id: user.id,
                    size: '5'
                })
            }
            columns={{
                columns: ['receivers', 'senders', 'state'],
                headerOverwrite: new Map([
                    ['receivers', CommonMessage.OBJECTS.TRANSACTION.RECEIVERS],
                    ['senders', CommonMessage.OBJECTS.TRANSACTION.SENDERS],
                    ['state', CommonMessage.OBJECTS.COMMON_FIELDS.STATE]
                ]),
                customCells: createParticipantActivityCells<LaundryTransaction>()
            }}
            type={EntityType.TRANSACTION}
            tableProps={{ hideFooter: true, maxHeight: true }}
        />
    );
};

export const UserActivityVendingSection = ({ user }: { user: IUser }) => {
    const dispatch = useAppDispatch();
    const navigate = useNavigate();
    const { closeAllModals } = useModals();
    return (
        <ReactiveObjectList<ExtendedVendingTransaction>
            dispatch={dispatch}
            key={`recent-vendings-${user.id}`}
            label={RecentVendings}
            fetch={(arg) =>
                TransactionVendingClient.getVendingTransactions({
                    ...arg,
                    application: Application.VENDING,
                    user_id: user.id,
                    size: '5'
                })
            }
            columns={{
                columns: ['ean', 'sku', 'product', 'quantity', 'state', 'type'],
                headerOverwrite: new Map([
                    ['state', CommonMessage.OBJECTS.COMMON_FIELDS.STATE],
                    ['ean', CommonMessage.OBJECTS.PRODUCT.EAN],
                    ['sku', CommonMessage.OBJECTS.PRODUCT.SKU],
                    ['product', CommonMessage.OBJECTS.PRODUCT.DEFAULT],
                    ['quantity', CommonMessage.OBJECTS.COMMON_FIELDS.QUANTITY],
                    ['type', CommonMessage.OBJECTS.COMMON_FIELDS.TYPE]
                ]),
                customCells: new Map([
                    [
                        'state',
                        (row) => (
                            <td
                                key={row.row.original.state}
                                className='text-foreground-default truncate p-2 text-sm font-medium'>
                                <TransactionStateBadge state={row.row.original.state} />
                            </td>
                        )
                    ],
                    [
                        'product',
                        (row) => {
                            if (!row.row.original.product) {
                                return (
                                    <td className='text-brand-primary align-center truncate p-2 text-xs'>
                                        <div className='text-foreground-default justify-start text-sm font-medium'>/</div>
                                    </td>
                                );
                            }
                            return (
                                <td
                                    key={row.row.original.product.entity_id}
                                    className='text-brand-primary align-center truncate p-2 text-xs'>
                                    <div className='flex gap-2.5 truncate text-sm font-medium'>
                                        <PlatformObjectBadge
                                            id={row.row.original.product.entity_id}
                                            entityType={EntityType.PRODUCT}
                                            display={row.row.original.product.name}
                                            foreign={row.row.original.product.foreign}
                                            accountId={accountIdFromResolvedEntity(row.row.original.product)}
                                        />
                                    </div>
                                </td>
                            );
                        }
                    ],
                    [
                        'type',
                        (row) => (
                            <td className={defaultCellClassName}>
                                <div className='text-foreground-default justify-start text-sm font-medium'>
                                    {getVendingTransactionTypeDisplay(row.row.original.feature)}
                                </div>
                            </td>
                        )
                    ]
                ])
            }}
            type={EntityType.TRANSACTION}
            tableProps={{
                customFooter: (
                    <div
                        onClick={async () => {
                            navigate(`/vending/transactions?user=${user.unique_identifier}`);
                            await new Promise(() => setTimeout(closeAllModals, 100));
                        }}
                        className='bg-background-surface text-brand-primary hover:bg-brand-primary hover:text-background-surface border-brand-primary inline-flex items-center justify-center gap-2 rounded-md border px-4 py-3 hover:cursor-pointer'>
                        <div className='justify-start text-base font-medium'>
                            <FormattedMessage
                                id='transaction.viewDetailedList'
                                defaultMessage='View detailed list'
                            />
                        </div>
                    </div>
                ),
                maxHeight: true
            }}
        />
    );
};

export const UserActivityLendingSection = ({ user }: { user: IUser }) => {
    const dispatch = useAppDispatch();
    const navigate = useNavigate();
    const { closeAllModals } = useModals();
    return (
        <ReactiveObjectList<ExtendedLendingTransaction>
            dispatch={dispatch}
            key={`recent-utilizations-${user.id}`}
            label={RecentUtilizations}
            fetch={(arg) =>
                TransactionLendingClient.fetchLendingTransactions({
                    ...arg,
                    account_id: user.account_id,
                    application: Application.LENDING,
                    user_id: user.id,
                    size: '5'
                })
            }
            columns={{
                columns: ['product', 'equipment', 'quantity', 'state'],
                headerOverwrite: new Map([
                    ['product', CommonMessage.OBJECTS.PRODUCT.DEFAULT],
                    ['equipment', CommonMessage.OBJECTS.EQUIPMENT.DEFAULT],
                    ['quantity', CommonMessage.OBJECTS.COMMON_FIELDS.QUANTITY],
                    ['state', CommonMessage.OBJECTS.COMMON_FIELDS.STATE]
                ]),
                customCells: new Map([
                    [
                        'state',
                        (row) => (
                            <td
                                key={row.row.original.state}
                                className='text-foreground-default truncate p-2 text-sm font-medium'>
                                <TransactionStateBadge state={row.row.original.state} />
                            </td>
                        )
                    ],
                    [
                        'product',
                        (row) => {
                            if (!row.row.original.product) {
                                return (
                                    <td className='text-brand-primary align-center truncate p-2 text-xs'>
                                        <div className='text-foreground-default justify-start text-sm font-medium'>/</div>
                                    </td>
                                );
                            }
                            return (
                                <td
                                    key={row.row.original.product.entity_id}
                                    className='text-brand-primary align-center truncate p-2 text-xs'>
                                    <div className='flex gap-2.5 truncate text-sm font-medium'>
                                        <PlatformObjectBadge
                                            id={row.row.original.product.entity_id}
                                            entityType={EntityType.PRODUCT}
                                            display={row.row.original.product.name}
                                            foreign={row.row.original.product.foreign}
                                            accountId={accountIdFromResolvedEntity(row.row.original.product)}
                                        />
                                    </div>
                                </td>
                            );
                        }
                    ],
                    [
                        'equipment',
                        (row) => {
                            if (!row.row.original.equipment) {
                                return (
                                    <td className='text-brand-primary align-center truncate p-2 text-xs'>
                                        <div className='text-foreground-default justify-start text-sm font-medium'>/</div>
                                    </td>
                                );
                            }
                            return (
                                <td
                                    key={row.row.original.equipment.entity_id}
                                    className='text-brand-primary align-center truncate p-2 text-xs'>
                                    <div className='flex gap-2.5 truncate text-sm font-medium'>
                                        <PlatformObjectBadge
                                            id={row.row.original.equipment.entity_id}
                                            entityType={EntityType.EQUIPMENT}
                                            display={row.row.original.equipment.name}
                                            foreign={row.row.original.equipment.foreign}
                                            accountId={accountIdFromResolvedEntity(row.row.original.equipment)}
                                        />
                                    </div>
                                </td>
                            );
                        }
                    ]
                ])
            }}
            type={EntityType.TRANSACTION}
            tableProps={{
                customFooter: (
                    <div
                        onClick={async () => {
                            navigate(`/lending/transactions?user_id=${user.id}`);
                            await new Promise(() => setTimeout(closeAllModals, 100));
                        }}
                        className='bg-background-surface text-brand-primary hover:bg-brand-primary hover:text-background-surface border-brand-primary inline-flex items-center justify-center gap-2 rounded-md border px-4 py-3 hover:cursor-pointer'>
                        <div className='justify-start text-base font-medium'>
                            <FormattedMessage
                                id='transaction.viewDetailedList'
                                defaultMessage='View detailed list'
                            />
                        </div>
                    </div>
                ),
                maxHeight: true
            }}
        />
    );
};

/** Licensed applications that expose a user-activity dropdown option. */
export function useUserActivityApplications(): Application[] {
    const { functionalities } = useLicenseRepository();
    const available = new Set(functionalities.map((functionality) => functionality.application));
    return [Application.PUDO, Application.LAUNDRY, Application.VENDING, Application.LENDING].filter((application) => available.has(application));
}

const UserEventContent = ({ event, locale }: { event: Event; locale?: string }) => (
    <div className='flex flex-col gap-2'>
        <p className={getHistoryEventTitleClassName(event)}>{mapEventDataToDisplay(event.data)}</p>
        <HistoryEventDetails
            event={event}
            timestamp={formatDateTimeWithLocale(new Date(event.creation_timestamp), locale)}
        />
        <PlatformEventReferenceBadges
            event={event}
            excludeKeys={['user_id']}
        />
    </div>
);

export const UserEventsSection = ({ user }: { user: IUser }) => {
    const { locale } = useLocale();
    return (
        <div className='bg-background-surface outline-border-default inline-flex w-full flex-col items-start justify-start gap-6 self-stretch rounded-xl p-4 outline-1 outline-offset-[-1px]'>
            <div className='text-foreground-default justify-start text-xl font-semibold'>{CommonMessage.DETAIL.EVENT_LOGS}</div>
            <TransactionHistory
                key='events'
                entityType={EntityType.USER}
                entityId={user.id}
                showChildren
                queryParams={{ level: [EventLevel.INFO, EventLevel.ERROR] }}
                content={(event) => (
                    <UserEventContent
                        event={event}
                        locale={locale}
                    />
                )}
            />
        </div>
    );
};
